/**
 * TradeStack - Expense Form Page (Create/Edit)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import '../styles/Forms.css';

function ExpenseForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { config, expenseCategories } = useConfig();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingExpense, setLoadingExpense] = useState(isEditMode);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const [formData, setFormData] = useState({
    vendor: '',
    description: '',
    amount: '',
    category: '',
    expense_date: new Date().toISOString().split('T')[0],
    job_id: '',
    is_tax_deductible: true,
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    loadJobs();
    if (isEditMode) {
      loadExpense();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadJobs = async () => {
    try {
      const response = await api.getJobs({ limit: 1000 });
      console.log('ExpenseForm - Jobs loaded:', response.data.jobs);
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadExpense = async () => {
    try {
      const response = await api.getExpense(id);
      const expense = response.data.expense;
      setFormData({
        vendor: expense.vendor || '',
        description: expense.description || '',
        amount: expense.amount || '',
        category: expense.category || '',
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
        job_id: expense.job_id || '',
        is_tax_deductible: expense.is_tax_deductible !== false,
        payment_method: expense.payment_method || 'cash',
        notes: expense.notes || ''
      });
    } catch (err) {
      setError('Failed to load expense');
      console.error(err);
    } finally {
      setLoadingExpense(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        job_id: formData.job_id || null
      };

      if (isEditMode) {
        await api.updateExpense(id, submitData);
        navigate(`/expenses`);
      } else {
        await api.createExpense(submitData);
        navigate(`/expenses`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} expense`);
    } finally {
      setLoading(false);
    }
  };

  // Debug logging for categories
  console.log('ExpenseForm - expenseCategories from context:', expenseCategories);
  console.log('ExpenseForm - config?.expenseCategories:', config?.expenseCategories);

  // Default categories fallback (use if config hasn't loaded yet)
  const defaultCategories = [
    { id: 'materials', name: 'Materials & Supplies' },
    { id: 'fuel', name: 'Gasoline/Fuel' },
    { id: 'tools', name: 'Tools & Equipment' },
    { id: 'vehicle', name: 'Vehicle Expenses' },
    { id: 'insurance', name: 'Insurance' },
    { id: 'subcontractor', name: 'Subcontractor Labor' },
    { id: 'rent', name: 'Rent/Storage' },
    { id: 'utilities', name: 'Utilities' },
    { id: 'software', name: 'Software/Subscriptions' },
    { id: 'office', name: 'Office Supplies' },
    { id: 'marketing', name: 'Marketing/Advertising' },
    { id: 'meals', name: 'Business Meals' },
    { id: 'travel', name: 'Travel' },
    { id: 'permits', name: 'Permits & Licenses' },
    { id: 'other', name: 'Other' }
  ];

  // Use expenseCategories from context if available, otherwise fallback
  const categories = (expenseCategories && expenseCategories.length > 0)
    ? expenseCategories
    : (config?.expenseCategories?.length > 0 ? config.expenseCategories : defaultCategories);

  const paymentMethods = [
    { id: 'cash', name: 'Cash' },
    { id: 'check', name: 'Check' },
    { id: 'credit_card', name: 'Credit Card' },
    { id: 'debit_card', name: 'Debit Card' },
    { id: 'bank_transfer', name: 'Bank Transfer' },
    { id: 'other', name: 'Other' }
  ];

  if (loadingExpense) {
    return <div className="form-page"><div className="loading-state">Loading...</div></div>;
  }

  return (
    <div className="form-page">
      <div className="detail-header">
        <div className="header-left">
          <Link to="/expenses" className="back-link">&larr; Back to Expenses</Link>
          <h1>{isEditMode ? 'Edit Expense' : 'New Expense'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3>Expense Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vendor">Vendor / Store *</label>
              <input
                type="text"
                id="vendor"
                name="vendor"
                value={formData.vendor}
                onChange={handleChange}
                required
                placeholder="e.g., Home Depot, Menards"
              />
            </div>
            <div className="form-group">
              <label htmlFor="amount">Amount *</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                min="0.01"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="What was purchased?"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Category --</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="expense_date">Date *</label>
              <input
                type="date"
                id="expense_date"
                name="expense_date"
                value={formData.expense_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="payment_method">Payment Method</label>
              <select
                id="payment_method"
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
              >
                {paymentMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="job_id">Link to Job (Optional)</label>
              <select
                id="job_id"
                name="job_id"
                value={formData.job_id}
                onChange={handleChange}
                disabled={loadingJobs}
              >
                <option value="">-- No Job --</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_tax_deductible"
                checked={formData.is_tax_deductible}
                onChange={handleChange}
              />
              <span>Tax Deductible</span>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>Notes</h3>
          <div className="form-group">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to="/expenses" className="btn btn-secondary">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Expense')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ExpenseForm;
